import { CommonModule } from '@angular/common';
import {
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    NgZone,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { filter, sortBy } from 'lodash-es';
import { PhotoContestApiService } from 'apps/account/src/account-shared/services/photo-contest-api.service';
import { TranslateService } from '@ngx-translate/core';
import { SessionService } from 'account-hybrid/core/authentication';
import { SpacesService } from 'account-hybrid/common/components/spaces';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';
import { buildAzureStoredPhotoUrl } from 'apps/account/src/account-shared/pipes/azure-stored-photo.util';
import { NativeCurationModalService } from '../../../common/components/curation-modal/native/native-curation-modal.service';
import { LegacyShimsModule } from '../../../../account-shared/legacy-shims/legacy-shims.module';
import { CurationBackgroundImageDirective } from '../../stories/curation-card/background-image.directive';
import { TranslateModule } from '@ngx-translate/core';
import { FtImageComponent } from './ft-image.component';
import dayjs from 'dayjs/esm/index';

/**
 * Contest SUBMISSIONS sheet: submission stats + the new/curated photo grids.
 * Loads via PhotoContestApiService and opens the native curation modal
 * (`curationModal.new`), mirroring its finalist/pass/photo-updated events.
 * Inert v1 custom elements (`<ft-ranking>`, `<ft-sheet-tabs>`) are kept as-is,
 * hence CUSTOM_ELEMENTS_SCHEMA.
 */
@Component({
    selector: 'ft-contest-submissions-ng',
    imports: [CommonModule, LegacyShimsModule, CurationBackgroundImageDirective, TranslateModule, FtImageComponent],
    encapsulation: ViewEncapsulation.None,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    templateUrl: './contest-submissions.component.html',
    styleUrls: ['./contest-submissions.component.scss'],
})
export class FtContestSubmissionsComponent implements OnInit, OnDestroy {
    readonly contestUuid = input.required<string>();
    /** v1 `$scope.reviewFinalists = onReviewFinalists` → switches the adapter to the Winners tab. */
    readonly reviewFinalists = output<void>();

    protected readonly screen = signal<'new' | 'curated'>('new');
    protected readonly model = signal<any>({
        paging: { SortBy: 'score', SortDirection: 'desc', Offset: 0, Fetch: 24 },
        // False until the first page lands: the Curated grid is not gated on a loaded flag, so a
        // `true` here would show "Load more" over the empty grid while the first page is in flight.
        hasMoreData: false,
        totalCurated: 0,
        scrollLoadsResetCounter: 0,
        contest: { submissions: { item: [] } },
        contestInfo: undefined,
        finalistsIncentives: undefined,
        submissionsWithPaging: [],
    });

    private readonly photos: any[] = [];
    private readonly stateMap: { [key: number]: string };
    /** A "Load more" request is in flight — a second click must not fetch the same page again. */
    private isLoadingNextPage = false;

    private readonly photoContest: any = inject(PhotoContestApiService);
    private readonly spaces: any = inject(SpacesService);
    private readonly session: any = inject(SessionService);
    // TERMINAL CURATION CUT: native opener (same .new/.on/.removeListener API).
    private readonly curationModal: any = inject(NativeCurationModalService);
    private readonly dayjs: any = dayjs;
    private readonly azurePhoto: (photo: any, size: string) => string;
    private readonly translate: (key: string) => string;
    private readonly zone = inject(NgZone);
    private readonly environment = inject<Environment>(ENVIRONMENT_TOKEN);

    private readonly onFinalist = (photoUUID: string) => this.onCurationFinalist(photoUUID);
    private readonly onPass = (photoUUID: string) => this.onCurationPass(photoUUID);
    private readonly onPhotoUpdated = (photoUUID: string) => this.onCurationPhotoUpdated(photoUUID);

    constructor() {
        this.azurePhoto = (photo: any, size: string) =>
            buildAzureStoredPhotoUrl(photo, size, this.environment.AzureCdnBaseUrl) as string;
        const translateSvc = inject(TranslateService);
        this.translate = (key: string, params?: any) => translateSvc.instant(key, params);
        this.stateMap = {
            0: '',
            1: this.translate('flipto.account.sections.platform.photoAdvocacy.Passed'),
            2: this.translate('flipto.account.sections.platform.photoAdvocacy.New'),
            3: this.translate('flipto.account.sections.platform.photoAdvocacy.Finalist'),
        };
    }

    ngOnInit(): void {
        const propertyUuid = this.spaces.getPropertyUuid();
        const paging = this.model().paging;

        Promise.all([
            Promise.resolve(this.photoContest.load({ contestId: this.contestUuid(), propertyUuid })),
            Promise.resolve(this.photoContest.submissions(propertyUuid, this.contestUuid())).then((data: any) => {
                let newCount = 0;
                if (data && data.submissions && data.submissions.item) {
                    // Default sort is creation date (desc), then push passed curations to the bottom.
                    data.submissions.item = sortBy(data.submissions.item, (s: any) => s.createDate).reverse();
                    data.submissions.item = sortBy(data.submissions.item, (s: any) => s.isPassed);
                    data.submissions.item.forEach((s: any) => {
                        if (s.isNewSubmission) newCount++;
                    });
                }
                this.screen.set(newCount === 0 ? 'curated' : 'new');
                this.photoStorageInit(data, data.submissions ? data.submissions.item : null);
                return data;
            }),
            Promise.resolve(this.photoContest.submissionsWithPaging(propertyUuid, this.contestUuid(), paging)),
        ]).then((data: any[]) => {
            this.zone.run(() => {
                this.model().finalistsIncentives = data[0].finalistsIncentives;
                this.model().contest = data[1];
                this.model().contestInfo = data[0];
                this.model().submissionsWithPaging = data[2];
                const now = this.dayjs();
                this.model().isContestRunning =
                    now.isAfter(this.model().contestInfo.startDate) && now.isBefore(this.model().contestInfo.votingEndDate);
                this.photoStorageInit(this.model().contest, this.model().submissionsWithPaging);
                this.model().hasMoreData = data[2]?.length === paging.Fetch;
                // Every row carries the count across all pages.
                this.model().totalCurated = data[2]?.[0]?.totalRecords ?? 0;
            });
        });

        // Listen to curation modal events (mirrors the v1 controller's fluent .on chain).
        this.curationModal.on('finalist', this.onFinalist);
        this.curationModal.on('pass', this.onPass);
        this.curationModal.on('photo-updated', this.onPhotoUpdated);
    }

    ngOnDestroy(): void {
        this.curationModal?.removeListener?.('finalist', this.onFinalist);
        this.curationModal?.removeListener?.('pass', this.onPass);
        this.curationModal?.removeListener?.('photo-updated', this.onPhotoUpdated);
    }

    protected get newSubmissions(): any[] {
        return (this.model().contest?.submissions?.item || []).filter((s: any) => s.isNewSubmission === true);
    }

    protected get curatedSubmissions(): any[] {
        return this.model().submissionsWithPaging || [];
    }

    protected get pluralCount(): number {
        return this.screen() === 'new' ? this.newSubmissions.length : this.model().totalCurated;
    }

    protected submissionStateName(submission: any): string {
        const state = submission.isPassed ? 1 : submission.isNewSubmission ? 2 : submission.isFinalist ? 3 : 0;
        return this.stateMap[state];
    }

    /** v1 `$scope.openPhotoCuration` — opens the curation modal (bridged until Step 3). */
    protected openPhotoCuration(submission: any): void {
        this.curationModal.new(this.session.getProperty(this.spaces.getPropertyUuid()), submission.guestUUID, true);
    }

    protected onReviewFinalists(): void {
        this.reviewFinalists.emit();
    }

    /**
     * v1 `$scope.model.loadNextPage` — appends the next page of curated submissions. The offset only
     * advances once a page lands, so a failed request can be retried.
     */
    protected loadNextPage(): void {
        if (this.isLoadingNextPage) return;
        this.isLoadingNextPage = true;
        const paging = { ...this.model().paging, Offset: this.model().paging.Offset + this.model().paging.Fetch };

        Promise.resolve(this.photoContest.submissionsWithPaging(this.spaces.getPropertyUuid(), this.contestUuid(), paging)).then(
            (data: any) => {
                this.zone.run(() => {
                    // Past the last row the proc returns an empty body, which the service hands back as undefined.
                    const page = Array.isArray(data) ? data : [];
                    // Index the new rows for the curation modal's events. The podium entries were
                    // indexed with the first page, hence no contest here.
                    this.photoStorageInit(null, page);
                    this.model.update((m: any) => ({
                        ...m,
                        paging,
                        hasMoreData: page.length === paging.Fetch,
                        submissionsWithPaging: [...m.submissionsWithPaging, ...page],
                    }));
                    this.isLoadingNextPage = false;
                });
            },
            () => {
                this.isLoadingNextPage = false;
            },
        );
    }

    private photoStorageInit(data: any, submissions: any[] | null): void {
        const photoKeys = ['firstPlace', 'secondPlace', 'thirdPlace'];
        if (submissions) {
            submissions.forEach((submission: any) => {
                submission.photoUrl = this.azurePhoto(submission, 'small');
                this.photos.push(submission);
            });
        }
        if (data && data.runnerUps && data.runnerUps.item) {
            const items = Array.isArray(data.runnerUps.item) ? data.runnerUps.item : [data.runnerUps.item];
            items.forEach((runnerUp: any) => {
                runnerUp.photoUrl = this.azurePhoto(runnerUp, 'thumbnail');
                this.photos.push(runnerUp);
            });
        }
        if (data) {
            photoKeys.forEach((key) => {
                if (!data[key]) return;
                data[key].photoUrl = this.azurePhoto(data[key], 'thumbnail');
                this.photos.push(data[key]);
            });
        }
    }

    private onCurationFinalist(photoUUID: string): void {
        this.zone.run(() => {
            const selected = filter(this.photos, { photoUUID: photoUUID.toLowerCase() });
            this.countIfNewlyCurated(selected);
            selected.forEach((photo: any) => {
                photo.isNewSubmission = false;
                photo.isFinalist = true;
                photo.isPassed = false;
            });
        });
    }

    private onCurationPass(photoUUID: string): void {
        this.zone.run(() => {
            const selected = filter(this.photos, { photoUUID: photoUUID.toLowerCase() });
            const contest = this.model().contest;
            this.countIfNewlyCurated(selected);
            selected.forEach((photo: any) => {
                photo.isNewSubmission = false;
                photo.isFinalist = false;
                photo.isPassed = true;
                if (contest.totalNewPhotoSubmissions > 0) {
                    contest.totalNewPhotoSubmissions -= 1;
                }
            });
        });
    }

    /** Call before the curation flags are written: a submission leaving New adds one to the Curated total. */
    private countIfNewlyCurated(selected: any[]): void {
        if (selected.some((photo: any) => photo.isNewSubmission)) {
            this.model().totalCurated += 1;
        }
    }

    private onCurationPhotoUpdated(photoUUID: string): void {
        this.zone.run(() => {
            const selected = filter(this.photos, { photoUUID: photoUUID.toLowerCase() });
            selected.forEach((photo: any) => {
                photo.isUpdated = true;
                photo.photoUrl = this.azurePhoto(photo, 'small');
            });
        });
    }
}
